import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Users, Search, User as UserIcon, Phone, X, ChevronRight, CheckCircle2, Pause, XCircle, AlertTriangle, RefreshCw, ClipboardList, Archive, Download, Palmtree, UserPlus, Salad, Baby, SlidersHorizontal } from "lucide-react";
import { useApiData, apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { applyOverridesToList } from "../lib/clientOverrides.js";
import Card from "../components/Card.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import { PruhFiltru, FiltrRadek, Zapinatko, Stitek, EmptyState, SkeletonRows, Hlaska } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import MultiSelectFilter from "../components/MultiSelectFilter.jsx";
import { prefetchClientData } from "../lib/prefetchClient.js";
import { formatDate as fmtDate, balicekKratce } from "../lib/format.js";
import ClientDetail from "./ClientDetail.jsx";
import LeadyPrehled from "../components/LeadyPrehled.jsx";
import KartaKontaktu from "../components/sales/KartaKontaktu.jsx";
import { prewarmLogExpiredCache } from "../lib/logExpiredCache.js";

const STAV_META = {
  "Aktivní":    { label: "Aktivní",    color: "emerald", Icon: CheckCircle2 },
  "Přerušeno":  { label: "Přerušeno",  color: "amber",   Icon: Pause },
  "Ukončeno":   { label: "Ukončeno",   color: "slate",   Icon: XCircle },
};

// Rychlý filtr nad seznamem (Flexible úvodní, Masterplány, Výživa, Děti,
// Archiv). 5.9.2026: na telefonu je to dlaždice ve třech sloupcích s kratším
// popiskem, takže se všechny vejdou na dva řádky. Na počítači zůstává původní
// řádkové tlačítko s celým popiskem. Chování ani počty se nemění.
const RYCHLY_FILTR_BARVY = {
  sky: { vyp: "bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100", zap: "bg-sky-600 text-white border-sky-600", odznak: "bg-sky-200 text-sky-800" },
  plum: { vyp: "bg-plum-50 text-plum-700 border-plum-200 hover:bg-plum-100", zap: "bg-plum-600 text-white border-plum-600", odznak: "bg-plum-200 text-plum-800" },
  emerald: { vyp: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100", zap: "bg-emerald-600 text-white border-emerald-600", odznak: "bg-emerald-200 text-emerald-800" },
  amber: { vyp: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100", zap: "bg-amber-500 text-white border-amber-500", odznak: "bg-amber-200 text-amber-800" },
  slate: { vyp: "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100", zap: "bg-slate-700 text-white border-slate-700", odznak: "bg-slate-200 text-slate-700" },
};
function RychlyFiltr({ zapnuto, onClick, ikona: Ikona, popis, popisKratky, pocet, barva = "slate", title, uzka }) {
  const b = RYCHLY_FILTR_BARVY[barva] || RYCHLY_FILTR_BARVY.slate;
  if (uzka) {
    return (
      <button type="button" onClick={onClick} title={title} aria-pressed={zapnuto}
        className={`relative flex min-h-[60px] flex-col items-center justify-center gap-1 rounded-xl border px-1 text-center transition-colors ${zapnuto ? b.zap : b.vyp}`}>
        <Ikona className="h-4 w-4" />
        <span className="text-[11px] font-bold leading-tight">{popisKratky || popis}</span>
        <span className={`absolute right-1.5 top-1 rounded-full px-1.5 text-[11px] font-bold ${zapnuto ? "bg-white/25 text-white" : b.odznak}`}>{pocet}</span>
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} title={title} aria-pressed={zapnuto}
      className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-semibold transition-colors ${zapnuto ? b.zap : b.vyp}`}>
      <Ikona className="h-3.5 w-3.5" /> {popis}
      <span className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-xs font-bold ${zapnuto ? "bg-white/25 text-white" : b.odznak}`}>{pocet}</span>
    </button>
  );
}

// 5.9.2026 (fáze 4b): tón ze vzorníku místo vlastní tabulky barev.
const STAV_TON = { "Aktivní": "ok", "Přerušeno": "pozor", "Ukončeno": "neutralni" };
function StavBadge({ stav }) {
  const meta = STAV_META[stav] || { label: stav || "?", color: "slate", Icon: UserIcon };
  return <Stitek ton={STAV_TON[stav] || "neutralni"} velikost="mala">{meta.label}</Stitek>;
}


function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return fmtDate(d);
  } catch { return iso; }
}


// Loose normalizace: lowercase + bez diakritiky + bez interpunkce + collapse mezery
function normalizeLoose(s) {
  return String(s || "").toLowerCase().trim()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[.,;:'"!?]/g, "")
    .replace(/\s+/g, " ");
}

function tokenizeLoose(s) {
  return normalizeLoose(s).split(" ").filter((t) => t.length >= 2);
}

// Levenshtein distance — pro tolerování drobných překlepů (Zimmermanová vs Zimmermannová,
// Lucie vs Lucia atd.). Pokud diff délky > 3, vrátí early 99 (heuristika pro performance).
function levenshtein(a, b) {
  if (!a || !b) return Math.max((a || "").length, (b || "").length);
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 3) return 99;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

// Fuzzy lookup klienta: prochází 4 strategie od nejstriktnější po nejvolnější.
// Vrací klient objekt nebo null.
function findClientFuzzy(clients, query) {
  if (!query || !clients?.length) return null;
  const qNorm = normalizeLoose(query);
  const qSorted = qNorm.split(" ").sort().join(" ");
  const qTokens = tokenizeLoose(query);

  // 1. Exact normalized match (s tolerancí pořadí slov)
  for (const c of clients) {
    const cNorm = normalizeLoose(c.klient);
    if (cNorm === qNorm) return c;
    const cSorted = cNorm.split(" ").sort().join(" ");
    if (cSorted === qSorted) return c;
  }

  // 2. Token subset (≥ 2 společných tokenů — pro plně shodné víceslovné jména)
  for (const c of clients) {
    const cTokens = tokenizeLoose(c.klient);
    if (cTokens.length < 2 || qTokens.length < 2) continue;
    const common = qTokens.filter((t) => cTokens.includes(t));
    if (common.length >= 2) return c;
  }

  // 3. Levenshtein distance ≤ 2 — chytá překlepy (Zimmermanová↔Zimmermannová, Lucie↔Lucia)
  let bestMatch = null;
  let bestDist = 3; // strict cap: max 2 znakové změny
  for (const c of clients) {
    const cSorted = normalizeLoose(c.klient).split(" ").sort().join(" ");
    const dist = levenshtein(qSorted, cSorted);
    if (dist < bestDist) {
      bestDist = dist;
      bestMatch = c;
    }
  }
  if (bestMatch) return bestMatch;

  // 4. Substring fallback obousměrný
  const qLow = query.toLowerCase().trim();
  for (const c of clients) {
    const cLow = (c.klient || "").toLowerCase();
    if (cLow.includes(qLow) || qLow.includes(cLow)) return c;
  }

  return null;
}


// Flexible "úvodní" — klient s balíčkem FLEXIBLE, který je v prvním měsíci spolupráce
// (první vystavená flexible faktura v posledních 30 dnech) a je aktivní. CSM se mu má ozvat, jestli
// bude ve flexible pokračovat, nebo přejde na jiný balíček.
function isFlexUvodni(c) {
  if (!c || c.isLead) return false;
  if (c.stav !== "Aktivní") return false;
  if (!String(c.balicek || "").toLowerCase().includes("flexible")) return false;
  // 21.7.2026: okno "prvniho mesice" se pocita od PRVNI vystavene FLEXIBLE faktury,
  // ne od Zacatku spoluprace (ten byval prazdny/stary a klient nespravne vypadl z filtru).
  const z = c.prvniFlexibleFaktura ? new Date(c.prvniFlexibleFaktura) : null;
  if (!z || isNaN(z.getTime())) return false;
  const days = (Date.now() - z.getTime()) / 86400000;
  return days >= 0 && days <= 30;
}

// Karta leadu (sales KartaKontaktu) v okně nad seznamem, stejný obal jako ClientDetail:
// na mobilu přes celou obrazovku, na počítači vycentrované okno, Esc nebo klik vedle zavře.
function OknoLeadu({ id, onClose, onZmena }) {
  const okno = useRef(null);
  useEffect(() => {
    // Esc zavírá okno, jen když nad ním není otevřený dialog nebo nabídka (vykreslují se
    // portálem přímo do body a Esc si berou samy). Capture fáze: dřív, než se dialog zavře.
    const onKey = (e) => { if (e.key === "Escape" && ![...document.querySelectorAll('body > [class*="fixed"]')].some((el) => el !== okno.current)) onClose?.(); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  // Portál do body: obsah stránky má transformaci, fixed by se jinak vztahoval k ní, ne k oknu prohlížeče.
  return createPortal(
    <div ref={okno} className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
      <div className="flex min-h-full items-start justify-center p-0 sm:p-6">
        <div className="min-h-screen w-full max-w-3xl bg-slate-50 p-2 shadow-2xl sm:my-4 sm:min-h-0 sm:rounded-2xl sm:p-4" onClick={(e) => e.stopPropagation()}>
          <KartaKontaktu key={id} id={id} onZavrit={onClose} onZmena={onZmena} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function Clients() {
  const { data, setData, loading, error, refetch, refreshing } = useApiData("/api/clients");
  const [searchParams, setSearchParams] = useSearchParams();
  // 7. 10. 2026 (#139, Láďa): karta leadu se otevírá tady v Klientech (?lead=<id>), ne v sekci Sales.
  const leadKarta = Number(searchParams.get("lead")) || null;
  const otevriLead = (id) => { const next = new URLSearchParams(searchParams); next.set("lead", String(id)); setSearchParams(next); };
  const zavriLead = () => { const next = new URLSearchParams(searchParams); next.delete("lead"); setSearchParams(next); };
  const initialSearch = searchParams.get("search") || "";
  // 3. 11. 2026 (Láďa): po archivaci nebo vrácení z archivu se lead v přehledu přesune hned
  // (data z /api/clients mají cache 120 s), pak se načtou čerstvá data (refetch = force=1).
  const zmenaLeadu = (r) => {
    if (r && r.leadId && (r.akce === "archivovat" || r.akce === "z_archivu")) {
      const arch = r.akce === "archivovat";
      setData((d) => (d && Array.isArray(d.clients) ? { ...d, clients: d.clients.map((c) => (c.isLead && Number(c.leadId) === Number(r.leadId) ? { ...c, archivovan: arch, leadArchiv: arch ? (r.archiv || { kdy: new Date().toISOString(), kdo: "" }) : null } : c)) } : d));
    }
    refetch();
  };
  const shouldAutoOpen = searchParams.get("open") === "1";
  // Nutritionist (13.7.2026): vidí jen klienty — bez tabů Leady/Vše a bez
  // CSM filtru "Flexible úvodní". Masterplany chip zůstává (její hlavní filtr).
  const jeNutri = getCurrentUser()?.role === "nutritionist";

  // Prewarm Log Expired cache na pozadí — uživatel zatím prochází seznam klientů,
  // než klikne na detail, cache je už zahřátá → klient detail s propadlými = instant.
  useEffect(() => {
    prewarmLogExpiredCache(new Date().getFullYear());
  }, []);

  // 10.7.2026 — miniatury profilovek z portálu (64 px, pár kB na klientku).
  // Klíč = notion id bez pomlček, do avatarů v tabulce místo iniciál.
  const [fotky, setFotky] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch("/api/client-photos");
        const j = await r.json();
        if (!cancelled && j.ok) setFotky(j.fotky || {});
      } catch (_) { /* ticho — bez fotek zůstanou iniciály */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const [search, setSearch] = useState(initialSearch);
  // Tab: clients (jen klienti = isLead=false, default), leads (jen leady), all (vše)
  const [recordType, setRecordType] = useState(searchParams.get("lead") ? "leads" : "clients");
  // Filtry jsou vícevýběrové — pole vybraných hodnot, prázdné pole = "Vše".
  const [filterStav, setFilterStav] = useState([]);
  // Filter Stage — pro tab Leady (Lead/Kvalifikace/NQLead/Trial/Prohra atd.)
  const [filterStage, setFilterStage] = useState([]);
  const [filterTrener, setFilterTrener] = useState([]);
  const [filterLokalita, setFilterLokalita] = useState([]);
  const [filterTyp, setFilterTyp] = useState("all");
  const [filterBalicek, setFilterBalicek] = useState([]);
  const [filterKonec, setFilterKonec] = useState([]); // soon30 / soon90 / ended / openEnded
  // Stav treninku filter: over3 (V poradku, >3) / under3 (1-2, Brzy dojde) / zero (0 a min, Vycerpano)
  const [filterStavTreninku, setFilterStavTreninku] = useState([]);
  // Posledni trenink filter: inactive15 (15+ dni bez treninku)
  const [filterPosledniTrenink, setFilterPosledniTrenink] = useState([]);
  // Faktura status filter: paid / overdue / pending / nofapi (zadny FAPI match)
  const [filterFaktura, setFilterFaktura] = useState([]);
  // Flexible úvodní: jen klienti s balíčkem FLEXIBLE v prvním měsíci spolupráce (k oslovení CSM).
  const [filterFlexUvodni, setFilterFlexUvodni] = useState(false);
  // Masterplány (12.7.2026, pro nutritionist): jen aktivní klienti s aktivním
  // masterplánem; v řádku badge s plněním za posledních 14 dní.
  const [filterMasterplan, setFilterMasterplan] = useState(false);
  // Archiv (13.7.2026, owner): archivovaní klienti (data.archivovan) jsou
  // defaultně skrytí všem; owner je chipem zobrazí a může obnovit/smazat.
  const [filterArchiv, setFilterArchiv] = useState(false);
  // Výživa (13.7.2026): jen klienti s typem spolupráce Výživový koučink.
  const [filterVyziva, setFilterVyziva] = useState(false);
  // Děti (17.7.2026): rodiče s aktivní dětskou permicí (akce léto 2026).
  const [filterDeti, setFilterDeti] = useState(false);
  // Instantní zobrazení chipu: mapa se drží v sessionStorage a fetch ji jen
  // tiše obnoví na pozadí (jinak chip po otevření sekce na moment problikl).
  const [mpPrehled, setMpPrehled] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("fw40_mp_prehled") || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch("/api/masterplan-prehled");
        const j = await r.json();
        if (!cancelled && j.ok) {
          setMpPrehled(j.klienti || {});
          try { sessionStorage.setItem("fw40_mp_prehled", JSON.stringify(j.klienti || {})); } catch (_) { /* plné úložiště nevadí */ }
        }
      } catch (_) { /* ticho — bez masterplan dat filtr prostě nesvítí */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const mpProKlienta = (c) => {
    const nid = (((c.notion || "").match(/[0-9a-f]{32}/i) || [])[0] || "").toLowerCase();
    const mp = nid && mpPrehled[nid];
    return mp && mp.ukoly > 0 ? mp : null;
  };
  const jeAktivniStav = (c) => String(c.stav || "").toLowerCase().startsWith("aktiv");
  // Výživoví klienti (13.7.2026): typ spolupráce Výživový koučink — bez tréninků.
  // POZOR: musí být deklarováno PŘED useMemo `filtered`, které ji volá (TDZ).
  const jeVyzivovyKlient = (c) => /v[yý][žz]iv/i.test(String(c.typSpoluprace || ""));
  const [selected, setSelected] = useState(null);
  const [autoOpenDone, setAutoOpenDone] = useState(false);
  // 5.9.2026 (fáze 4b): na telefonu se místo tabulky kreslí karty a filtry
  // sloupců se přesouvají do rozbalovacího panelu (jinak nejsou dostupné).
  const uzka = useUzkaObrazovka();
  const [filtryOtevrene, setFiltryOtevrene] = useState(false);
  // Optimisticky skryté smazané leady (owner delete) — zmizí ze seznamu ihned, než doběhne refetch.
  const [deletedKeys, setDeletedKeys] = useState(() => new Set());

  // Aplikuj client overrides z localStorage (po edit klienta v detail uvidí user
  // novou hodnotu okamžitě, i když sync klienti cron ještě neproběhl).
  const clients = useMemo(
    () => applyOverridesToList(data?.clients || []).filter((c) => !deletedKeys.has(c.klient)),
    [data?.clients, selected, deletedKeys]
  );

  // Filter dropdowns — odstranujeme historicke / nerelevantni hodnoty (zadani Ladislav 7.5.2026):
  // - Treneri: skryt Anastasiia Vozniuk (byvala), Lada Zatecka (mam), Tinka (mama)
  // - Lokality: pouze 2 oficialni (VT GYM, Sportovni hala Kbely). Sjednocujeme 4 varianty pravopisu.
  // - Typ spoluprace: jen kategorie Trenink (Vyziva, Konzultace atd. patri jinam)
  const HIDDEN_TRENERS = ["Anastasiia Vozniuk", "Láďa Žatečka", "Tinka"];
  // Mapovaci tabulka — Notion / Sheet ma ruzne pravopisy ("Praha - Kbely - VT GYM",
  // "VT GYM - Kbely", "Sportovni Hala Kbely", "Kbely - Sportovni hala") → 2 canonical hodnoty.
  function canonicalLokalita(raw) {
    const s = String(raw || "").toLowerCase();
    if (!s) return "";
    if (s.includes("vt gym")) return "VT GYM";
    if (s.includes("sportovní hala") || s.includes("sportovni hala")) return "Sportovní hala Kbely";
    return ""; // ostatni lokality nas nezajimaji (zadani 7.5.2026)
  }
  const ALLOWED_TYP_SPOLUPRACE = ["Úvodní", "Neomezená", "Permice"];
  const trainers = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => { if (c.trener) set.add(c.trener); });
    return [...set].filter((t) => !HIDDEN_TRENERS.includes(t)).sort();
  }, [clients]);

  const lokality = useMemo(() => {
    // Sjednoceni pres canonicalLokalita, vraci jen 2 oficialni (VT GYM + Sportovni hala Kbely).
    const set = new Set();
    clients.forEach((c) => {
      const canon = canonicalLokalita(c.lokalita);
      if (canon) set.add(canon);
    });
    return ["VT GYM", "Sportovní hala Kbely"].filter((l) => set.has(l));
  }, [clients]);

  const typySpoluprace = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => { if (c.typSpoluprace) set.add(c.typSpoluprace); });
    return ALLOWED_TYP_SPOLUPRACE.filter((t) => set.has(t));
  }, [clients]);

  const balicky = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => { if (c.balicek) set.add(c.balicek); });
    return [...set].sort();
  }, [clients]);

  // Helper — kolik dní zbývá do data konce (záporné = už proběhl)
  function daysUntilEnd(konecIso) {
    if (!konecIso) return null;
    const d = new Date(konecIso);
    if (isNaN(d)) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((d - today) / (1000 * 60 * 60 * 24));
  }

  const filtered = useMemo(() => {
    // Search: tolerantní k diakritice (lapacko ≡ Lapačková) + multi-token
    // (např. "aneta lap" najde "Aneta Lapačková" v jakémkoli pořadí).
    const sNorm = normalizeLoose(search);
    const sTokens = sNorm ? sNorm.split(" ").filter((t) => t.length >= 1) : [];
    return clients.filter((c) => {
      // Tab filter: clients = !isLead, leads = isLead, all = oba
      if (recordType === "clients" && c.isLead) return false;
      if (recordType === "leads" && !c.isLead) return false;
      // Vícevýběrové filtry: prázdné pole = bez omezení, jinak hodnota musí být v poli (OR).
      if (filterStav.length && !filterStav.includes(c.stav)) return false;
      if (filterStage.length && !filterStage.includes(c.stage)) return false;
      if (filterTrener.length && !filterTrener.includes(c.trener)) return false;
      if (filterLokalita.length && !filterLokalita.includes(canonicalLokalita(c.lokalita))) return false;
      if (filterTyp !== "all" && c.typSpoluprace !== filterTyp) return false;
      if (filterBalicek.length && !filterBalicek.includes(c.balicek)) return false;
      if (filterFlexUvodni && !isFlexUvodni(c)) return false;
      if (filterMasterplan && !(mpProKlienta(c) && jeAktivniStav(c))) return false;
      // Archivovaní: skrytí všude; chip Archiv (owner) ukáže JEN je.
      if (c.archivovan && !filterArchiv) return false;
      // 3. 11. 2026 (audit): Archiv je archiv klientů, archivované leady mají dlaždici v záložce Leady
      // (v záložce Vše by jinak seznam neseděl s počtem archivCount).
      if (filterArchiv && (c.isLead || !c.archivovan)) return false;
      if (filterVyziva && !jeVyzivovyKlient(c)) return false;
      if (filterDeti && !c.detiAktivni) return false;
      if (filterKonec.length) {
        const days = daysUntilEnd(c.konec);
        const match = filterKonec.some((f) => {
          if (f === "openEnded") return !c.konec;
          if (f === "ended") return days !== null && days < 0;
          if (f === "soon30") return days !== null && days >= 0 && days <= 30;
          if (f === "soon90") return days !== null && days >= 0 && days <= 90;
          return false;
        });
        if (!match) return false;
      }
      // Stav treninku — pocet zbyvajicich treninku (po prepocitani fakturace - odchozenych)
      if (filterStavTreninku.length) {
        const n = parseInt(c.stavTreninku, 10);
        if (isNaN(n)) return false;
        const match = filterStavTreninku.some((f) => {
          if (f === "over3") return n > 3;
          if (f === "under3") return n >= 1 && n <= 2;
          if (f === "zero") return n <= 0;
          return false;
        });
        if (!match) return false;
      }
      // Posledni trenink — neaktivni 15+ dni
      if (filterPosledniTrenink.includes("inactive15")) {
        if (!c.posledniTrenink) return false; // nema posledni trenink → ignore
        const lastD = new Date(c.posledniTrenink);
        if (isNaN(lastD)) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const days = Math.floor((today - lastD) / (1000 * 60 * 60 * 24));
        if (days <= 15) return false;
      }
      // Faktura status filter (vícevýběr, OR)
      if (filterFaktura.length) {
        const match = filterFaktura.some((f) => f === "nofapi" ? !c.fapiLastInvoiceStatus : c.fapiLastInvoiceStatus === f);
        if (!match) return false;
      }
      if (sTokens.length > 0) {
        const hay = normalizeLoose(`${c.klient} ${c.email} ${c.telefon} ${c.balicek}`);
        // Všechny tokeny musí být v hay (substring) — pořadí nehraje
        if (!sTokens.every((t) => hay.includes(t))) return false;
      }
      return true;
    });
  }, [clients, recordType, search, filterStav, filterStage, filterTrener, filterLokalita, filterTyp, filterBalicek, filterKonec, filterStavTreninku, filterPosledniTrenink, filterFaktura, filterFlexUvodni, filterMasterplan, filterArchiv, filterVyziva, filterDeti, mpPrehled]);

  // CSV export aktuálního seznamu (owner + CSM, 17.7.2026). Exportuje to, co je právě
  // vidět v tabulce (respektuje tab i filtry). Středník kvůli českému Excelu, BOM kvůli diakritice.
  const muzeExportCsv = ["owner", "csm"].includes(getCurrentUser()?.role);
  const exportCsv = () => {
    const esc = (v) => {
      const s = String(v ?? "").replace(/\r?\n/g, " ");
      return /[";]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const cols = [
      ["Klient", (c) => c.klient],
      ["Stav", (c) => c.stav],
      ["Typ záznamu", (c) => (c.isLead ? "Lead" : "Klient")],
      ["Typ spolupráce", (c) => c.typSpoluprace],
      ["Balíček", (c) => c.balicek],
      ["Trenér", (c) => c.trener],
      ["Lokalita", (c) => c.lokalita],
      ["E-mail", (c) => c.email],
      ["Telefon", (c) => c.telefon],
      ["Začátek", (c) => c.zacatek],
      ["Konec", (c) => c.konec],
      ["Zbývá tréninků", (c) => c.stavTreninku],
      ["Poslední trénink", (c) => c.posledniTrenink],
      ["Na dovolené", (c) => (c.naDovolene ? "ano" : "")],
    ];
    const lines = [cols.map((c) => c[0]).join(";")];
    for (const c of filtered) lines.push(cols.map(([, get]) => esc(get(c))).join(";"));
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `klienti-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  // Counters pro tab UI (z full clients, ne z filtered)
  const totalKlienti = useMemo(() => clients.filter((c) => !c.isLead).length, [clients]);
  // Počet "Flexible úvodní" klientů (k oslovení CSM) — pro chip s počtem.
  const flexUvodniCount = useMemo(() => clients.filter(isFlexUvodni).length, [clients]);
  const masterplanCount = useMemo(
    () => clients.filter((c) => mpProKlienta(c) && jeAktivniStav(c)).length,
    [clients, mpPrehled] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const vyzivaCount = useMemo(() => clients.filter((c) => jeVyzivovyKlient(c) && !c.archivovan).length, [clients]); // eslint-disable-line react-hooks/exhaustive-deps
  const detiCount = useMemo(() => clients.filter((c) => c.detiAktivni && !c.archivovan).length, [clients]);

  // Archivovaní (owner) — backend je posílá jen ownerovi (ostatní role je nedostanou vůbec).
  const jeOwner = getCurrentUser()?.role === "owner";
  // 3. 11. 2026: archivované leady (archivovan na leadu) mají vlastní dlaždici v záložce Leady,
  // do archivu klientů ani do počtu leadů se nepočítají.
  const archivCount = useMemo(() => clients.filter((c) => !c.isLead && c.archivovan).length, [clients]);
  const totalLeady = useMemo(() => clients.filter((c) => c.isLead && !c.archivovan).length, [clients]);

  // Stage counter pro Leady tab pills
  const leadStageCounts = useMemo(() => {
    const counts = {};
    clients.forEach((c) => {
      if (!c.isLead || c.archivovan) return;
      const s = c.stage || "(nezarazeno)";
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [clients]);

  const totalAktivni = data?.byStav?.["Aktivní"] || 0;
  const totalPrerusen = data?.byStav?.["Přerušeno"] || 0;
  const totalUkoncen = data?.byStav?.["Ukončeno"] || 0;

  // 5.9.2026 (fáze 4b): jeden seznam voleb pro filtr ve sloupci tabulky
  // (počítač) i pro rozbalovací panel na telefonu. Obsah je stejný jako dřív.
  const stavItems = [
    totalAktivni > 0 && { key: "Aktivní", label: `Aktivní (${totalAktivni})`, ton: "ok" },
    totalPrerusen > 0 && { key: "Přerušeno", label: `Přerušeno (${totalPrerusen})`, ton: "pozor" },
    totalUkoncen > 0 && { key: "Ukončeno", label: `Ukončeno (${totalUkoncen})`, ton: "neutralni" },
  ].filter(Boolean);
  const stageItems = ["Lead", "Kvalifikace", "Trial", "Trial proběhl", "NQLead", "Prohra"]
    .filter((x) => leadStageCounts[x])
    .map((x) => ({ key: x, label: `${x} (${leadStageCounts[x]})` }));
  const trenerItems = trainers.map((t) => ({ key: t, label: t }));
  const lokalitaItems = lokality.map((l) => ({ key: l, label: l }));
  const balicekItems = balicky.map((b) => ({ key: b, label: b }));
  const fakturaItems = [
    { key: "overdue", label: "Po splatnosti", ton: "problem" },
    { key: "pending", label: "Vystavená", ton: "info" },
    { key: "paid", label: "Zaplacená", ton: "ok" },
    { key: "nofapi", label: "Bez faktury", ton: "neutralni" },
  ];
  const treninkyItems = [
    { key: "over3", label: "> 3 (v pořádku)", ton: "ok" },
    { key: "under3", label: "1 až 2 (brzy dojde)", ton: "pozor" },
    { key: "zero", label: "0 a méně (vyčerpáno)", ton: "problem" },
  ];
  const konecItems = [
    { key: "soon30", label: "Do 30 dní", ton: "problem" },
    { key: "soon90", label: "Do 90 dní", ton: "pozor" },
    { key: "ended", label: "Skončilo", ton: "neutralni" },
    { key: "openEnded", label: "Bez data", ton: "neutralni" },
  ];
  const posledniItems = [{ key: "inactive15", label: "Neaktivní 15+ dní", ton: "problem" }];

  const zrusVsechnyFiltry = () => {
    setSearch(""); setFilterStav([]); setFilterStage([]); setFilterTrener([]);
    setFilterLokalita([]); setFilterTyp("all"); setFilterBalicek([]); setFilterKonec([]);
    setFilterStavTreninku([]); setFilterPosledniTrenink([]); setFilterFaktura([]);
    setFilterFlexUvodni(false); setFilterMasterplan(false); setFilterArchiv(false); setFilterVyziva(false);
  };

  // Auto-open detail: po načtení dat se zkusíme proklikntout rovnou na klienta z URL
  useEffect(() => {
    if (!shouldAutoOpen || autoOpenDone || !data || !initialSearch) return;
    // Robustní fuzzy lookup: exact → token subset → Levenshtein ≤ 2 → substring
    // Toleruje překlepy (Zimmermanová↔Zimmermannová), záměny (Lucie↔Lucia),
    // diakritiku (Bártková↔Bartková), pořadí slov a interpunkci.
    const match = findClientFuzzy(clients, initialSearch);
    if (match) {
      setSelected(match);
      // Sync search input na canonické jméno (kvůli filter UI). Pokud Zimmermanová → match
      // Zimmermannová, input se aktualizuje aby filter ukázal pravého klienta v listu.
      if (match.klient && match.klient !== initialSearch) {
        setSearch(match.klient);
      }
      // Odeber ?open=1 z URL, ať se modal znovu neotevře při refreshi
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
    setAutoOpenDone(true);
  }, [data, clients, shouldAutoOpen, autoOpenDone, initialSearch, searchParams, setSearchParams]);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Gradient header — sjednoceno s ostatními stránkami (LogExpired, Pravidla, Automatizace).
          Eyebrow + title + subtitle + Obnovit tlačítko vpravo. */}
      {/* 5.9.2026 (fáze 4b): podtitulek byl jedna dlouhá věta („520 klientů
          celkem, 89 aktivních, 3 přerušených, 83 ukončených"), na telefonu se
          lámala na dva řádky a čísla se v ní ztrácela. Teď jsou čísla ve
          vlastním pruhu pod hlavičkou a akce jsou ikony, ne široká tlačítka. */}
      <GradientHeader
        title="Klienti"
        icon={Users}
        loading={loading}
        akce={[
          ...(muzeExportCsv ? [{
            klic: "csv",
            ikona: Download,
            popis: "Stáhnout CSV se zobrazeným seznamem (respektuje filtry)",
            onClick: exportCsv,
            disabled: loading || filtered.length === 0,
          }] : []),
          {
            klic: "obnovit",
            ikona: RefreshCw,
            popis: refreshing ? "Načítám…" : "Obnovit",
            onClick: refetch,
            disabled: loading || refreshing,
          },
        ]}
      />

      {data && (
        <div className="glass grid grid-cols-3 gap-px overflow-hidden bg-slate-200 !p-0 sm:max-w-md">
          <div className="flex min-h-[58px] flex-col items-center justify-center bg-white px-2 py-2">
            <span className="text-[11px] font-semibold text-slate-500">Aktivní</span>
            <span className="text-lg font-bold tabular-nums text-emerald-700">{totalAktivni}</span>
          </div>
          <div className="flex min-h-[58px] flex-col items-center justify-center bg-white px-2 py-2">
            <span className="text-[11px] font-semibold text-slate-500">Přerušeno</span>
            <span className="text-lg font-bold tabular-nums text-amber-700">{totalPrerusen}</span>
          </div>
          <div className="flex min-h-[58px] flex-col items-center justify-center bg-white px-2 py-2">
            <span className="text-[11px] font-semibold text-slate-500">Ukončeno</span>
            <span className="text-lg font-bold tabular-nums text-slate-500">{totalUkoncen}</span>
          </div>
        </div>
      )}

      <Card
        lista
        ikona={Users}
        title={recordType === "leads" ? "Seznam leadů" : recordType === "all" ? "Seznam klientů a leadů" : "Seznam klientů"}
        odznak={data ? `${filtered.length} z ${clients.length}` : undefined}
      >
        {/* Tab prepinac: Klienti / Leady / Vse — leady pridani 7.5.2026.
            Klient = zaznam v Stav 2026 (uzavreny deal). Lead = jen v Notion/Leady (sales pipeline). */}
        {/* 5.9.2026 (fáze 4b): na telefonu jde přepínač přes celou šířku
            (tři stejné díly) a rychlé filtry do tří sloupců, aby se vešly
            na dva řádky. Na počítači zůstává původní řada. */}
        <div className={`mb-4 gap-3 ${uzka ? "space-y-2" : "flex flex-wrap items-center"}`}>
          <div className={`items-center gap-1 rounded-xl bg-slate-100 p-1 ${uzka ? "grid grid-cols-3" : "flex w-fit"}`}>
            {(jeNutri ? [
              { value: "clients", label: "Klienti", count: totalKlienti },
            ] : [
              { value: "clients", label: "Klienti", count: totalKlienti },
              { value: "leads",   label: "Leady",   count: totalLeady },
              { value: "all",     label: "Vše",     count: totalKlienti + totalLeady },
            ]).map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setRecordType(tab.value)}
                className={`rounded-xl text-sm font-semibold transition-all ${uzka ? "min-h-[44px] px-2 text-center" : "px-4 py-1.5"} ${
                  recordType === tab.value
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 text-xs ${recordType === tab.value ? "text-slate-500" : "text-slate-400"}`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Na telefonu tři sloupce (pět filtrů se vejde na dva řádky),
              na počítači se obal chová jako by tam nebyl. */}
          <div className={uzka ? "grid grid-cols-3 gap-2" : "contents"}>

          {/* Flexible úvodní — rychlý filtr pro CSM: klienti s balíčkem FLEXIBLE v prvním měsíci
              spolupráce, kterým se má CSM ozvat a domluvit pokračování. Vedle tabů. */}
          {!jeNutri && recordType !== "leads" && flexUvodniCount > 0 && (
            <RychlyFiltr uzka={uzka} barva="sky" ikona={Phone} popis="Flexible úvodní" popisKratky="Flexible"
              pocet={flexUvodniCount} zapnuto={filterFlexUvodni} onClick={() => setFilterFlexUvodni((v) => !v)}
              title="Flexible v prvním měsíci spolupráce, ozvat se klientovi a domluvit, jestli pokračuje ve flexible nebo přejde na jiný balíček." />
          )}

          {/* Masterplány (12.7.2026) — aktivní klienti s aktivním masterplánem,
              v řádku hned vidět plnění. Hlavně pro výživovou specialistku. */}
          {recordType !== "leads" && masterplanCount > 0 && (
            <RychlyFiltr uzka={uzka} barva="plum" ikona={ClipboardList} popis="Masterplany" popisKratky="Masterplán"
              pocet={masterplanCount} zapnuto={filterMasterplan} onClick={() => setFilterMasterplan((v) => !v)}
              title="Aktivní klienti s aktivním masterplánem, štítek u jména ukazuje plnění za posledních 14 dní." />
          )}

          {/* Výživa (13.7.2026) — klienti s typem spolupráce Výživový koučink
              (bez tréninků a trenéra, jen výživové služby). */}
          {recordType !== "leads" && vyzivaCount > 0 && (
            <RychlyFiltr uzka={uzka} barva="emerald" ikona={Salad} popis={jeNutri ? "Výživový koučink" : "Výživa"} popisKratky="Výživa"
              pocet={vyzivaCount} zapnuto={filterVyziva} onClick={() => setFilterVyziva((v) => !v)}
              title="Výživoví klienti, typ spolupráce Výživový koučink: bez osobních tréninků, jen výživové služby (masterplán, jídelníček, konzultace)." />
          )}

          {/* Děti (17.7.2026) — rodiče s aktivní dětskou permicí (akce léto
              2026): zbývají tréninky a platnost neprošla. */}
          {recordType !== "leads" && detiCount > 0 && (
            <RychlyFiltr uzka={uzka} barva="amber" ikona={Baby} popis="Děti" popisKratky="Děti"
              pocet={detiCount} zapnuto={filterDeti} onClick={() => setFilterDeti((v) => !v)}
              title="Klienti s aktivní dětskou permanentkou, dětské tréninky: zbývají tréninky a platnost neprošla." />
          )}

          {/* Archiv (13.7.2026, jen owner) — archivovaní klienti: skrytí všem,
              tady je owner vidí, může je otevřít a obnovit / smazat. */}
          {jeOwner && recordType !== "leads" && archivCount > 0 && (
            <RychlyFiltr uzka={uzka} barva="slate" ikona={Archive} popis="Archiv" popisKratky="Archiv"
              pocet={archivCount} zapnuto={filterArchiv} onClick={() => setFilterArchiv((v) => !v)}
              title="Archivovaní klienti, nikdo je nevidí, v databázi zůstávají. V detailu je lze obnovit nebo smazat." />
          )}
          </div>
        </div>

        {/* 7. 10. 2026 (#139): záložka Leady má vlastní přehled podle stage, klepnutí otevře kartu kontaktu ze sales. */}
        {recordType === "leads" ? (
          <LeadyPrehled leady={clients.filter((c) => c.isLead)} loading={loading} error={error} onDetail={(c) => setSelected(c)} onKarta={(c) => otevriLead(c.leadId)} />
        ) : (<>
        {/* Search bar + Reset all v jedne radce */}
        {(() => {
          const multiFilters = [
            filterStav, filterStage, filterTrener, filterLokalita, filterBalicek, filterKonec,
            filterStavTreninku, filterPosledniTrenink, filterFaktura,
          ];
          const anyActive = multiFilters.some((f) => f.length > 0) || filterTyp !== "all" || !!search || filterFlexUvodni || filterMasterplan || filterArchiv || filterVyziva || filterDeti;
          const activeCount = multiFilters.filter((f) => f.length > 0).length
            + (filterTyp !== "all" ? 1 : 0) + (search ? 1 : 0) + (filterFlexUvodni ? 1 : 0) + (filterMasterplan ? 1 : 0) + (filterArchiv ? 1 : 0) + (filterVyziva ? 1 : 0) + (filterDeti ? 1 : 0);
          return (
            <>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={uzka ? "Hledat klienta" : "Hledat, i jen příjmení, bez háčků (lapacko ≡ Lapačková)"}
                  className={`field !pl-10 ${search ? "!border-2 !border-brand" : ""}`}
                />
              </div>
              {anyActive && !uzka && (
                <button
                  onClick={zrusVsechnyFiltry}
                  className="btn btn-secondary shrink-0"
                  title="Vynulovat všechny filtry"
                >
                  <X className="w-4 h-4" /> Zrušit vše
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 text-[11px] font-bold rounded-full bg-brand text-white px-1.5">{activeCount}</span>
                </button>
              )}
            </div>

            {/* Telefon: filtry sloupců tabulky se sem stěhují, jinak nejsou
                dostupné (schované sloupce). Schváleno 5.9.2026. */}
            {uzka && (
              <div className="mb-3 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFiltryOtevrene((o) => !o)}
                    aria-expanded={filtryOtevrene}
                    className={`flex min-h-[44px] flex-1 items-center gap-2 rounded-xl px-3 text-left ${
                      activeCount > 0 || filtryOtevrene
                        ? "border-2 border-brand bg-brand-50 font-bold text-brand-deep"
                        : "border border-slate-300 bg-white font-semibold text-slate-700"
                    }`}
                  >
                    <SlidersHorizontal className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-sm">Filtry</span>
                    {activeCount > 0 && (
                      <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-bold text-white">{activeCount}</span>
                    )}
                  </button>
                  {anyActive && (
                    <button type="button" onClick={zrusVsechnyFiltry} className="btn btn-secondary shrink-0">Zrušit vše</button>
                  )}
                </div>
                {filtryOtevrene && (
                  <PruhFiltru>
                    {recordType === "leads" ? (
                      <FiltrRadek stitek="Stage" items={stageItems} value={filterStage} onChange={setFilterStage} />
                    ) : (
                      <FiltrRadek stitek="Stav" items={stavItems} value={filterStav} onChange={setFilterStav} />
                    )}
                    <FiltrRadek stitek="Trenér" items={trenerItems} value={filterTrener} onChange={setFilterTrener} />
                    {lokalitaItems.length > 0 && <FiltrRadek stitek="Lokalita" items={lokalitaItems} value={filterLokalita} onChange={setFilterLokalita} />}
                    {balicekItems.length > 1 && <FiltrRadek stitek="Balíček" items={balicekItems} value={filterBalicek} onChange={setFilterBalicek} />}
                    <FiltrRadek stitek="Faktura" items={fakturaItems} value={filterFaktura} onChange={setFilterFaktura} />
                    <FiltrRadek stitek="Tréninky" items={treninkyItems} value={filterStavTreninku} onChange={setFilterStavTreninku} />
                    <FiltrRadek stitek="Konec" items={konecItems} value={filterKonec} onChange={setFilterKonec} />
                    <FiltrRadek stitek="Poslední" items={posledniItems} value={filterPosledniTrenink} onChange={setFilterPosledniTrenink} />
                    {!jeNutri && recordType !== "leads" && flexUvodniCount > 0 && (
                      <Zapinatko zapnuto={filterFlexUvodni} onZmena={() => setFilterFlexUvodni((v) => !v)} popisek={`Jen Flexible úvodní (${flexUvodniCount})`} ikona={Phone} />
                    )}
                    {recordType !== "leads" && masterplanCount > 0 && (
                      <Zapinatko zapnuto={filterMasterplan} onZmena={() => setFilterMasterplan((v) => !v)} popisek={`Jen s masterplánem (${masterplanCount})`} ikona={ClipboardList} />
                    )}
                    {recordType !== "leads" && vyzivaCount > 0 && (
                      <Zapinatko zapnuto={filterVyziva} onZmena={() => setFilterVyziva((v) => !v)} popisek={`Jen výživa (${vyzivaCount})`} ikona={Salad} />
                    )}
                    {recordType !== "leads" && detiCount > 0 && (
                      <Zapinatko zapnuto={filterDeti} onZmena={() => setFilterDeti((v) => !v)} popisek={`Jen děti (${detiCount})`} ikona={Baby} />
                    )}
                    {jeOwner && recordType !== "leads" && archivCount > 0 && (
                      <Zapinatko zapnuto={filterArchiv} onZmena={() => setFilterArchiv((v) => !v)} popisek={`Jen archiv (${archivCount})`} ikona={Archive} />
                    )}
                  </PruhFiltru>
                )}
                <div className="px-1 text-[11px] font-semibold text-slate-400">Zobrazeno {filtered.length} z {clients.length}</div>
              </div>
            )}
            </>
          );
        })()}

        {/* Tabulka klientů */}
        {loading && <SkeletonRows rows={8} />}
        {error && <Hlaska ton="problem" title="Nepovedlo se načíst klienty">{error}</Hlaska>}
        {!loading && !error && filtered.length === 0 && (
          <EmptyState icon={Users} title="Nikdo neodpovídá filtru" hint="Zkus zrušit filtry nebo změnit hledání." />
        )}
        {/* Telefon: karty místo tabulky. Ze široké tabulky byly na 390 px vidět
            jen dva sloupce, zbytek (i s filtry) byl schovaný. Schváleno 5.9.2026. */}
        {filtered.length > 0 && uzka && (
          <div className="space-y-2">
            {filtered.map((c, i) => {
              const dnyDoKonce = daysUntilEnd(c.konec);
              const jeBlacklist = (c.blacklist === "Ano" || c.blacklist === "ANO" || c.stav === "BLACKLIST");
              const pocet = parseInt(c.stavTreninku, 10);
              const tonPoctu = Number.isNaN(pocet) ? "neutralni" : pocet >= 3 ? "ok" : pocet >= 1 ? "pozor" : "problem";
              const nid = (((c.notion || "").match(/[0-9a-f]{32}/i) || [])[0] || "").toLowerCase();
              const foto = nid && fotky[nid];
              const mp = mpProKlienta(c);
              return (
                <button
                  key={c.klient + i}
                  type="button"
                  onClick={() => setSelected(c)}
                  className={`polozka flex w-full items-center gap-3 p-3 text-left ${jeBlacklist ? "border-l-[3px] border-l-rose-400" : ""} ${dnyDoKonce !== null && dnyDoKonce < 0 ? "opacity-60" : ""}`}
                >
                  <span className="relative shrink-0">
                    {foto ? (
                      <img src={foto} alt={c.klient} className={`h-11 w-11 rounded-full object-cover demo-sensitive ${c.naDovolene ? "ring-2 ring-sky-400" : ""}`} />
                    ) : (
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold ${jeBlacklist ? "bg-rose-200 text-rose-800" : "bg-brand-100 text-brand-700"} ${c.naDovolene ? "ring-2 ring-sky-400" : ""}`}>
                        {(c.klient || "?").trim().charAt(0).toUpperCase()}
                      </span>
                    )}
                    {c.naDovolene && (
                      <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-sky-500 text-white" title="Právě na dovolené">
                        <Palmtree className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-bold text-slate-900 demo-sensitive">{c.klient}</span>
                      {jeBlacklist && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-rose-600" title="Klient na BLACKLIST" />}
                      {c.hasDuplicateName && <UserPlus className="h-3.5 w-3.5 shrink-0 text-amber-600" title="Duplicitní jméno" />}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1">
                      <Stitek ton={STAV_TON[c.stav] || "neutralni"} velikost="mala">{c.stav || "?"}</Stitek>
                      {c.balicek && (
                        <Stitek ton="znacka" velikost="mala" className="max-w-[150px]" title={c.balicek}>
                          <span className="block min-w-0 truncate">{balicekKratce(c.balicek)}</span>
                        </Stitek>
                      )}
                      {c.trener && (
                        <Stitek ton="neutralni" velikost="mala" className="max-w-[130px] demo-sensitive" title={c.trener}>
                          <span className="block min-w-0 truncate">{c.trener}</span>
                        </Stitek>
                      )}
                      {mp && <Stitek ton={mp.plneni == null ? "neutralni" : mp.plneni >= 80 ? "ok" : mp.plneni >= 50 ? "pozor" : "problem"} velikost="mala" ikona={ClipboardList}>{mp.plneni == null ? "bez záznamu" : `${mp.plneni} %`}</Stitek>}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-0.5">
                    {!Number.isNaN(pocet) && (
                      <>
                        <Stitek ton={tonPoctu} velikost="bezna" className="tabular-nums">{pocet}</Stitek>
                        <span className="text-[11px] text-slate-400">tréninků</span>
                      </>
                    )}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                </button>
              );
            })}
          </div>
        )}
        {filtered.length > 0 && !uzka && (
          <div className="overflow-x-auto -mx-4 sm:-mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="text-left font-semibold py-3 px-4 sm:px-5">{recordType === "leads" ? "Lead" : "Klient"}</th>
                  <th className="text-left font-semibold py-3 px-2">{recordType === "leads" ? "Stage" : "Stav"}</th>
                  <th className="text-left font-semibold py-3 px-2 hidden sm:table-cell">Faktura</th>
                  <th className="text-left font-semibold py-3 px-2 hidden md:table-cell">{recordType === "leads" ? "Sales/Kouč" : "Trenér"}</th>
                  <th className="text-left font-semibold py-3 px-2 hidden lg:table-cell">Lokalita</th>
                  <th className="text-left font-semibold py-3 px-2 hidden lg:table-cell">Balíček</th>
                  <th className="text-left font-semibold py-3 px-2 hidden xl:table-cell">Konec</th>
                  <th className="text-right font-semibold py-3 px-2 hidden sm:table-cell">Stav tréninků</th>
                  <th className="text-left font-semibold py-3 px-2 hidden md:table-cell">Poslední trénink</th>
                  <th className="w-10"></th>
                </tr>
                {/* Filter radek pod hlavickami — vyber per sloupec, aktivni filter ma modry ring */}
                <tr className="border-b border-slate-200">
                  <th className="px-4 sm:px-5 py-2"></th>
                  <th className="px-2 py-2">
                    {(() => {
                      const isStage = recordType === "leads";
                      const options = isStage
                        ? ["Lead","Kvalifikace","Trial","Trial proběhl","NQLead","Prohra"]
                            .filter((s) => leadStageCounts[s])
                            .map((s) => ({ value: s, label: `${s} (${leadStageCounts[s]})` }))
                        : [
                            totalAktivni > 0 && { value: "Aktivní", label: `Aktivní (${totalAktivni})` },
                            totalPrerusen > 0 && { value: "Přerušeno", label: `Přerušeno (${totalPrerusen})` },
                            totalUkoncen > 0 && { value: "Ukončeno", label: `Ukončeno (${totalUkoncen})` },
                          ].filter(Boolean);
                      return (
                        <MultiSelectFilter
                          options={options}
                          selected={isStage ? filterStage : filterStav}
                          onChange={isStage ? setFilterStage : setFilterStav}
                        />
                      );
                    })()}
                  </th>
                  <th className="px-2 py-2 hidden sm:table-cell">
                    <MultiSelectFilter
                      title="Filtrovat dle stavu poslední FAPI faktury"
                      selected={filterFaktura}
                      onChange={setFilterFaktura}
                      options={[
                        { value: "overdue", label: "Po splatnosti" },
                        { value: "pending", label: "Vystavená" },
                        { value: "paid", label: "Zaplacená" },
                        { value: "nofapi", label: "Bez faktury" },
                      ]}
                    />
                  </th>
                  <th className="px-2 py-2 hidden md:table-cell">
                    <MultiSelectFilter
                      selected={filterTrener}
                      onChange={setFilterTrener}
                      options={trainers.map((t) => ({ value: t, label: t }))}
                    />
                  </th>
                  <th className="px-2 py-2 hidden lg:table-cell">
                    {lokality.length > 0 ? (
                      <MultiSelectFilter
                        selected={filterLokalita}
                        onChange={setFilterLokalita}
                        options={lokality.map((l) => ({ value: l, label: l }))}
                      />
                    ) : null}
                  </th>
                  <th className="px-2 py-2 hidden lg:table-cell">
                    {balicky.length > 1 ? (
                      <MultiSelectFilter
                        selected={filterBalicek}
                        onChange={setFilterBalicek}
                        options={balicky.map((b) => ({ value: b, label: b }))}
                      />
                    ) : null}
                  </th>
                  <th className="px-2 py-2 hidden xl:table-cell">
                    <MultiSelectFilter
                      selected={filterKonec}
                      onChange={setFilterKonec}
                      options={[
                        { value: "soon30", label: "Do 30 dní" },
                        { value: "soon90", label: "Do 90 dní" },
                        { value: "ended", label: "Skončilo" },
                        { value: "openEnded", label: "Bez data" },
                      ]}
                    />
                  </th>
                  <th className="px-2 py-2 hidden sm:table-cell">
                    <MultiSelectFilter
                      title="Filtrovat dle počtu zbývajících tréninků"
                      selected={filterStavTreninku}
                      onChange={setFilterStavTreninku}
                      options={[
                        { value: "over3", label: "> 3 (V pořádku)" },
                        { value: "under3", label: "1–2 (Brzy dojde)" },
                        { value: "zero", label: "0 a méně (Vyčerpáno)" },
                      ]}
                    />
                  </th>
                  <th className="px-2 py-2 hidden md:table-cell">
                    <MultiSelectFilter
                      title="Filtrovat klienty co dlouho nebyli na tréninku"
                      selected={filterPosledniTrenink}
                      onChange={setFilterPosledniTrenink}
                      options={[{ value: "inactive15", label: "Neaktivní 15+ dní" }]}
                    />
                  </th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => {
                  const days = daysUntilEnd(c.konec);
                  const isEndingSoon = days !== null && days >= 0 && days <= 30;
                  const isEnded = days !== null && days < 0;
                  // Modul 2B: BLACKLIST viditelnost. Klient je na BLACKLIST pokud má
                  // explicit flag = "Ano" NEBO (legacy) pokud Stav spolupráce = "BLACKLIST".
                  const isBlacklist = (c.blacklist === "Ano" || c.blacklist === "ANO" || c.stav === "BLACKLIST");
                  // Iniciála pro avatar (první písmeno jména)
                  const initial = (c.klient || "?").trim().charAt(0).toUpperCase();
                  // Barva avataru deterministicky podle jména
                  const avatarColors = [
                    "bg-blue-100 text-blue-700",
                    "bg-emerald-100 text-emerald-700",
                    "bg-brand-100 text-brand-700",
                    "bg-amber-100 text-amber-700",
                    "bg-rose-100 text-rose-700",
                    "bg-cyan-100 text-cyan-700",
                    "bg-pink-100 text-pink-700",
                    "bg-plum-100 text-plum-700",
                  ];
                  const colorIdx = (c.klient || "").charCodeAt(0) % avatarColors.length;
                  // Balíček chip color
                  const baliCkU = (c.balicek || "").toLowerCase();
                  let baliColor = "bg-slate-100 text-slate-700";
                  if (baliCkU.includes("premium")) baliColor = "bg-brand-50 text-brand-700 border border-brand-200";
                  else if (baliCkU.includes("gold")) baliColor = "bg-amber-50 text-amber-700 border border-amber-200";
                  else if (baliCkU.includes("flexible")) baliColor = "bg-blue-50 text-blue-700 border border-blue-200";
                  else if (baliCkU.includes("permanentka")) baliColor = "bg-emerald-50 text-emerald-700 border border-emerald-200";
                  else if (baliCkU.includes("výživ") || baliCkU.includes("vyziv")) baliColor = "bg-pink-50 text-pink-700 border border-pink-200";
                  return (
                    <tr
                      key={c.klient + i}
                      onClick={() => setSelected(c)}
                      onMouseEnter={() => prefetchClientData(c.klient)}
                      className={`border-b border-slate-100 hover:bg-blue-50/50 cursor-pointer transition-colors ${
                        isBlacklist ? "bg-rose-50/40 hover:bg-rose-50" : (isEnded ? "opacity-60" : "")
                      }`}
                    >
                      <td className="py-3 px-4 sm:px-5">
                        <div className="flex items-center gap-3 min-w-0">
                          {(() => {
                            // Profilovka z portálu (miniatura), fallback iniciály
                            const nid = (((c.notion || "").match(/[0-9a-f]{32}/i) || [])[0] || "").toLowerCase();
                            const foto = nid && fotky[nid];
                            const kruh = foto ? (
                              <img src={foto} alt={c.klient} className="w-9 h-9 rounded-full object-cover flex-shrink-0 demo-sensitive" title="Profilová fotka z klientského portálu" />
                            ) : (
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                                isBlacklist ? "bg-rose-200 text-rose-800" : avatarColors[colorIdx]
                              }`}>
                                {initial}
                              </div>
                            );
                            // Bublinka: klient má právě teď zadané volno typu dovolená.
                            // Výrazné provedení (17.7.): modrý ring + větší bublinka s bílým okrajem.
                            if (!c.naDovolene) return kruh;
                            return (
                              <div className="relative flex-shrink-0 rounded-full ring-[3px] ring-sky-400" title="Právě na dovolené">
                                {kruh}
                                <span className="absolute -bottom-1.5 -right-1.5 min-w-[22px] min-h-[22px] rounded-full bg-gradient-to-br from-sky-400 to-sky-600 border-2 border-white flex items-center justify-center text-white shadow-md"><Palmtree className="w-3 h-3" /></span>
                              </div>
                            );
                          })()}
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-900 demo-sensitive truncate flex items-center gap-1.5">
                              {c.klient}
                              {c.naDovolene && (
                                <Stitek ton="info" velikost="mala" ikona={Palmtree} className="shrink-0" title="Klient má právě teď zadané volno typu dovolená">Na dovolené</Stitek>
                              )}
                              {isBlacklist && (
                                <Stitek ton="problem" velikost="mala" ikona={AlertTriangle} title="Klient na BLACKLIST">Blacklist</Stitek>
                              )}
                              {c.hasDuplicateName && (
                                <Stitek ton="pozor" velikost="mala" ikona={AlertTriangle} title="Duplicitní jméno: existují 2 a více klientů se stejným jménem. Rezervace se přiřazují přes e-mail nebo telefon, pokud chybí, padají náhodně. Doporučení: přejmenovat jednoho z duplikátů.">Duplicitní jméno</Stitek>
                              )}
                              {c.referredByName ? (
                                <Stitek ton="znacka" velikost="mala" ikona={UserPlus} className="shrink-0 demo-sensitive" title={`Akce kamarádka, přivedla ${c.referredByName}`}>{c.referredByName}</Stitek>
                              ) : (c.kampanRef === "Akce kamarádka" && (
                                <Stitek ton="pozor" velikost="mala" ikona={UserPlus} className="shrink-0" title="Akce kamarádka, referrera se nepodařilo dohledat podle kódu, dopáruj ručně">Referral</Stitek>
                              ))}
                              {isFlexUvodni(c) && (
                                <Stitek ton="info" velikost="mala" ikona={Phone} className="shrink-0" title="Flexible, první měsíc spolupráce. Ozvi se klientovi a domluv, jestli pokračuje ve flexible nebo přejde na jiný balíček.">Úvodní</Stitek>
                              )}
                              {(() => {
                                // Masterplán badge — plnění za 14 dní na první pohled.
                                const mp = mpProKlienta(c);
                                if (!mp) return null;
                                const pct = mp.plneni;
                                const cls = pct == null ? "bg-slate-300 text-slate-700"
                                  : pct >= 80 ? "bg-emerald-500 text-white"
                                  : pct >= 50 ? "bg-amber-500 text-white"
                                  : "bg-rose-500 text-white";
                                return (
                                  <span
                                    className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${cls}`}
                                    title={`Masterplán: ${mp.ukoly} aktivních úkolů, plnění za 14 dní: ${pct == null ? "zatím bez záznamu" : pct + " %"}${mp.posledniZapis ? `, poslední zápis ${String(mp.posledniZapis).slice(0, 10)}` : ""}`}
                                  >
                                    <ClipboardList className="w-3 h-3" /> {pct == null ? "bez záznamu" : pct + " %"}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="text-xs text-slate-500 md:hidden truncate">{c.trener}</div>
                            {c.typSpoluprace && (
                              <div className="text-xs text-slate-500 hidden md:block">{c.typSpoluprace}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2"><StavBadge stav={c.stav} /></td>
                      <td className="py-3 px-2 hidden sm:table-cell">
                        {(() => {
                          const st = c.fapiLastInvoiceStatus;
                          if (!st) {
                            // No FAPI invoice — pokud cash klient ma posledni cash platbu, zobraz jako bezna Zaplacena.
                            // Info ze plati hotovost je v "Zpusob platby" ve Finance sekci klient detailu.
                            if (c.lastCashPaymentDate) {
                              return (
                                <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded-lg border bg-emerald-50 text-emerald-700 border-emerald-200" title={`Zaplaceno ${c.lastCashPaymentAmount ? c.lastCashPaymentAmount + ' Kč' : ''} (hotovostí)`}>
                                  Zaplacená
                                </span>
                              );
                            }
                            return <span className="text-slate-400 text-sm">—</span>;
                          }
                          const d = c.fapiLastInvoiceDaysOverdue;
                          if (st === "paid") {
                            const wasLate = d > 0;
                            const cls = wasLate
                              ? "bg-rose-50 text-rose-700 border-rose-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200";
                            return (
                              <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-lg border ${cls}`} title={wasLate ? `Zaplaceno ${d} dní po splatnosti` : "Zaplaceno včas"}>
                                Zaplacená{wasLate ? ` (${d}d po)` : ""}
                              </span>
                            );
                          }
                          if (st === "overdue") {
                            return (
                              <span className="inline-flex items-center text-xs font-semibold px-2 py-1 rounded-lg border bg-rose-50 text-rose-700 border-rose-200" title={`Po splatnosti ${d || "?"} dní`}>
                                Po splatnosti{d ? ` ${d}d` : ""}
                              </span>
                            );
                          }
                          // pending
                          return (
                            <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded-lg border bg-blue-50 text-blue-700 border-blue-200" title="Vystavená, čeká na úhradu">
                              Vystavená
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-2 hidden md:table-cell text-slate-700">{c.trener || "—"}</td>
                      <td className="py-3 px-2 hidden lg:table-cell">
                        {(() => {
                          const canon = canonicalLokalita(c.lokalita);
                          if (!canon) return <span className="text-slate-400 text-sm">—</span>;
                          const lokColor = canon === "VT GYM"
                            ? "bg-plum-50 text-plum-700 border-plum-200"
                            : "bg-cyan-50 text-cyan-700 border-cyan-200";
                          return (
                            <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-lg border ${lokColor}`} title={c.lokalita || ""}>
                              {canon}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-2 hidden lg:table-cell">
                        {c.balicek ? (
                          <span className={`inline-block text-xs font-medium px-2 py-1 rounded-lg max-w-[180px] truncate align-middle ${baliColor}`} title={c.balicek}>
                            {c.balicek}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 hidden xl:table-cell">
                        {c.konec ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg ${
                            isEnded ? "bg-slate-100 text-slate-500" :
                            isEndingSoon ? "bg-rose-50 text-rose-700 border border-rose-200" :
                            days !== null && days <= 90 ? "bg-amber-50 text-amber-700 border border-amber-200" :
                            "bg-slate-50 text-slate-600"
                          }`}>
                            {formatDate(c.konec)}
                            {isEndingSoon && days >= 0 && <span className="text-[11px] opacity-80">, za {days} dní</span>}
                          </span>
                        ) : (
                          // Bez data konce — pokud Neomezená spolupráce nebo už Ukončeno, je to OK ("—").
                          // Jinak alert: typ vyžaduje datum konce.
                          ((c.typSpoluprace || "").toLowerCase().includes("neomezen") || c.stav === "Ukončeno") ? (
                            <span className="text-slate-400 text-sm">—</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg bg-rose-50 text-rose-700 border border-rose-200" title={`Typ '${c.typSpoluprace}' má mít definované datum konce, doplň ho v kartě klienta`}>
                              <AlertTriangle className="w-3 h-3" /> Chybí konec
                            </span>
                          )
                        )}
                      </td>
                      <td className="py-3 px-2 hidden sm:table-cell text-right">
                        {(() => {
                          const stav = parseInt(c.stavTreninku, 10);
                          if (isNaN(stav)) return <span className="text-slate-400 text-sm">—</span>;
                          // Severity: 3+ zelená (OK), 1-2 amber (brzy dojde), ≤0 červená (vyčerpáno/mínus)
                          const cls =
                            stav >= 3 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                            stav >= 1 ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-rose-50 text-rose-700 border-rose-200";
                          return (
                            <span className={`inline-flex items-center justify-center min-w-[2.5rem] text-sm font-bold tabular-nums px-2 py-1 rounded-lg border ${cls}`}>
                              {stav}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-2 hidden md:table-cell text-sm">
                        {c.posledniTrenink ? (
                          (() => {
                            const d = new Date(c.posledniTrenink);
                            if (isNaN(d)) return <span className="text-slate-700">{c.posledniTrenink}</span>;
                            const today = new Date(); today.setHours(0, 0, 0, 0);
                            const days = Math.floor((today - d) / (1000 * 60 * 60 * 24));
                            const tone = days <= 7 ? "text-slate-700" : days <= 14 ? "text-amber-700" : "text-rose-700";
                            return (
                              <div className={tone}>
                                <div>{formatDate(c.posledniTrenink)}</div>
                                <div className="text-xs opacity-70">
                                  {days === 0 ? "dnes" : days === 1 ? "včera" : `před ${days} dny`}
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-slate-300 group-hover:text-slate-500"><ChevronRight className="w-4 h-4" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>)}
      </Card>

      {/* 8. 10. 2026: karta leadu se otevírá jako okno nad seznamem, stejně jako detail klientky. */}
      {recordType === "leads" && leadKarta && <OknoLeadu id={leadKarta} onClose={zavriLead} onZmena={zmenaLeadu} />}

      {/* Fullscreen detail (varianta D — ve stylu Total statistics) */}
      {selected && (
        <ClientDetail
          client={selected}
          onClose={() => setSelected(null)}
          restrictMode={
            getCurrentUser()?.role === "manager" ? "manager"
            : getCurrentUser()?.role === "nutritionist" ? "nutri"
            : undefined
          }
          onDeleted={() => {
            const key = selected?.klient;
            if (key) setDeletedKeys((prev) => new Set(prev).add(key));
            setSelected(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}
